import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from './baseQuery';
import { GetSellerProductsResponse, GetSellerProductsParams } from '../../types/products';

const transformProductDetailsResponse = (response: any) => {
  const payload = response.payload || response;
  
  if (!payload) return response;

  const transformedPayload = {
    ...payload,
    productSerial: payload.productSerial,
    categoryId: payload.category?.id || payload.categoryId,
    specialityId: payload.specialty?.id || payload.specialityId,
    speciality: payload.specialty || payload.speciality,
    // Logo
    logoImageId: payload.logoId || payload.logoImageId,
    logoImageUrl: payload.logoUrl || payload.logoImageUrl,
    logoName: payload.logoName,
    logoExtension: payload.logoExtension,
    logoBlobSize: payload.logoBlobSize,
    // Images
    productImages: (payload.images || payload.productImages || []).map((img: any) => ({
      id: img.id,
      url: img.url,
      isThumbnail: img.isDefault || img.isThumbnail || false,
      sortOrder: img.sortOrder || 0,
      name: img.name,
      extension: img.extension,
      blobSize: img.blobSize,
    })),
    // Tiers
    pricingTiers: (payload.tiers || payload.pricingTiers || []).map((tier: any) => ({
      id: tier.id,
      minQty: tier.from ?? tier.minQty,
      maxQty: tier.to ?? tier.maxQty,
      pricePerUnit: tier.price ?? tier.pricePerUnit,
      pricePerUnitConverted: tier.priceConverted ?? tier.pricePerUnitConverted,
      internalLabel: tier.label ?? tier.internalLabel,
    })),
    // Attributes
    attributes: (payload.attributes || []).map((attrObj: any) => ({
      attribute: {
        name: attrObj.attribute?.name || attrObj.name,
        values: (attrObj.attribute?.values || attrObj.values || []).map((v: any) => ({
          name: v.name,
          url: v.url,
          colorImageId: v.colorImageId,
        }))
      }
    })),
    // Variants
    variants: (payload.variants || []).map((variant: any) => ({
      id: variant.id,
      sku: variant.sku || '',
      stock: variant.stock || 0,
      price: variant.price,
      priceConverted: variant.priceConverted,
      attributes: (variant.attributes || []).map((attr: any) => ({
        categoryAttributeId: attr.attribute?.id || attr.categoryAttributeId,
        categoryAttributeDetailId: attr.values?.[0]?.id || attr.categoryAttributeDetailId,
        colorImageId: attr.values?.[0]?.colorImageId || attr.colorImageId,
        colorImageUrl: attr.values?.[0]?.url || attr.colorImageUrl,
        categoryAttributeName: attr.attribute?.name || attr.categoryAttributeName,
        categoryAttributeDetailName: attr.values?.[0]?.name || attr.categoryAttributeDetailName,
        nameEn: attr.attribute?.nameEn || attr.nameEn,
        nameAr: attr.attribute?.nameAr || attr.nameAr,
      })),
    })),
    // Certificates
    certificateDocs: (payload.certificates || payload.certificateDocs || []).map((cert: any) => ({
      id: cert.id,
      name: cert.name,
      url: cert.url,
      extension: cert.extension,
      blobSize: cert.blobSize,
    })),
    // Convert root properties to match the type
    priceType: payload.method?.code || payload.priceType,
    productPrice: payload.price || payload.productPrice,
    productPriceConverted: payload.priceConverted || payload.productPriceConverted,
    priceUnit: payload.price || payload.priceUnit,
    priceUnitConverted: payload.priceConverted || payload.priceUnitConverted,
    minQty: payload.minQty,
    stock: payload.stock,
    listedOn: payload.listedOn,
    cbm: payload.cm3 || payload.cbm,
    grossWeight: payload.grossWeight,
    height: payload.height,
    width: payload.width,
    length: payload.length,
    customizationTypes: payload.customizationTypes?.map((c: any) => c.code || c) || [],
    // Sample
    acceptSampleOrder: payload.canOrderSample || payload.acceptSampleOrder,
    samplePrice: payload.samplePrice,
    samplePriceConverted: payload.samplePriceConverted,
  };

  return { ...response, payload: transformedPayload };
};


export const productService = createApi({
  reducerPath: 'productService',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['ProductBasicDetails', 'SellerProductsList'],

  endpoints: (builder) => ({
    getProducts: builder.query({
      query: ({ page, search, fromDate, toDate, tag }) => ({
        url: `/product-service/v1/operator/product-requests`,
        method: 'GET',
        params: {
          page,
          searchField: search,
          fromDate,
          toDate,
          tag,
        },
      }),
    }),
    getProductsStats: builder.query({
      query: ({ page, search }) => ({
        url: `/product-service/v1/operator/product-requests/statistics`,
        method: 'GET',
        params: {
          page,
          search,
        },
      }),
    }),
    getProductDetails: builder.query({
      query: ({ productId }) => ({
        url: `/product-service/v1/operator/product-requests/${productId}`,
        method: 'GET',
      }),
      transformResponse: transformProductDetailsResponse,
    }),
    getProductBasicDetails: builder.query({
      query: ({ productId }) => ({
        url: `/product-service/v1/products/${productId}/basic-details`,
        method: 'GET',
      }),
    }),

    addFullCategory: builder.mutation({
      query: ({ body }) => ({
        url: `/v1/speciality-operator/full-category`,
        method: 'POST',
        body,
      }),
    }),

    editCategory: builder.mutation({
      query: ({ body }) => ({
        url: `/v1/speciality-operator/category`,
        method: 'PUT',
        body,
      }),
    }),

    approveProductRequest: builder.mutation({
      query: ({ productId }) => ({
        url: `/product-service/v1/operator/product-requests/${productId}/approve`,
        method: 'PUT',
      }),
    }),

    rejectProductRequest: builder.mutation({
      query: ({ productId, body }) => ({
        url: `/product-service/v1/operator/product-requests/${productId}/reject`,
        method: 'PUT',
        body,
      }),
    }),

    editProductRequest: builder.mutation({
      query: ({ productId, body }) => ({
        url: `/product-service/v1/operator/product-requests/${productId}/edit`,
        method: 'PUT',
        body,
      }),
    }),


    uploadProductImage: builder.mutation<any, FormData>({
      query: (body) => ({
        url: `/product-service/v1/product-attachments/images`,
        method: 'POST',
        body,
      }),
    }),
    uploadProductCertificate: builder.mutation<any, FormData>({
      query: (body) => ({
        url: `/product-service/v1/product-attachments/certificates`,
        method: 'POST',
        body,
      }),
    }),
    uploadProductColourImage: builder.mutation<any, FormData>({
      query: (body) => ({
        url: `/product-service/v1/product-attachments/color-image`,
        method: 'POST',
        body,
      }),
    }),
    uploadProductLogo: builder.mutation<any, FormData>({
      query: (body) => ({
        url: `/product-service/v1/product-attachments/logo`,
        method: 'POST',
        body,
      }),
    }),
    addSellerProduct: builder.mutation<any, any>({
      query: (body) => ({
        url: `/product-service/v1/products`,
        method: 'POST',
        body,
      }),
    }),
    updateSellerProduct: builder.mutation<any, any>({
      query: (body) => ({
        url: `/product-service/v1/products`,
        method: 'PUT',
        body,
      }),
    }),
    getSellerProductFullDetails: builder.query<any, string>({
      query: (productId) => ({
        url: `/product-service/v1/admin/products/${productId}`,
        method: 'GET',
      }),
      transformResponse: transformProductDetailsResponse,
    }),
    getProductBasicDetailsByAdmin: builder.query<any, string>({
      query: (productId) => ({
        url: `/product-service/v1/admin/products/${productId}/basic-details`,
        method: 'GET',
      }),
      transformResponse: transformProductDetailsResponse,
      providesTags: (_result, _error, productId) => [
        { type: 'ProductBasicDetails', id: productId },
      ],
    }),
    getSellerProductsByAdmin: builder.query<GetSellerProductsResponse, GetSellerProductsParams>({
      query: ({ sellerId, search, isPublish, stockStatuses, page, limit, fromDate, toDate }) => ({
        url: `/product-service/v1/admin/products/seller/${sellerId}`,
        method: 'GET',
        params: {
          search,
          isPublish,
          stockStatuses,
          page,
          limit,
          fromDate,
          toDate,
        },
      }),
      providesTags: [{ type: 'SellerProductsList', id: 'LIST' }],
    }),
    publishProduct: builder.mutation<any, { productId: string; publish: boolean }>({
      query: ({ productId, publish }) => ({
        url: `/product-service/v1/products/${productId}/publish`,
        method: 'PUT',
        body: { publish },
      }),
    }),
    addSellerProductByAdmin: builder.mutation<any, { sellerId: string; body: any }>({
      query: ({ sellerId, body }) => ({
        url: `/product-service/v1/admin/products/${sellerId}`,
        method: 'POST',
        body,
      }),
    }),
    editSellerProductByAdmin: builder.mutation<any, { productId: string; body: any }>({
      query: ({ productId, body }) => ({
        url: `/product-service/v1/admin/products/${productId}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (_result, _error, { productId }) => [
        { type: 'ProductBasicDetails', id: productId },
        { type: 'SellerProductsList', id: 'LIST' },
      ],
    }),
    deleteSellerProductByAdmin: builder.mutation<any, string>({
      query: (productId) => ({
        url: `/product-service/v1/admin/products/${productId}`,
        method: 'DELETE',
      }),
    }),
    uploadProductImageByAdmin: builder.mutation<any, FormData>({
      query: (body) => ({
        url: `/product-service/v1/admin/product-attachments/images`,
        method: 'POST',
        body,
      }),
    }),
    uploadProductCertificateByAdmin: builder.mutation<any, FormData>({
      query: (body) => ({
        url: `/product-service/v1/admin/product-attachments/certificates`,
        method: 'POST',
        body,
      }),
    }),
    uploadProductColorImageByAdmin: builder.mutation<any, FormData>({
      query: (body) => ({
        url: `/product-service/v1/admin/product-attachments/color-image`,
        method: 'POST',
        body,
      }),
    }),
    uploadProductLogoByAdmin: builder.mutation<any, FormData>({
      query: (body) => ({
        url: `/product-service/v1/admin/product-attachments/logo`,
        method: 'POST',
        body,
      }),
    }),
    publishProductByAdmin: builder.mutation<any, { productId: string; publish: boolean }>({
      query: ({ productId, publish }) => ({
        url: `/product-service/v1/admin/products/${productId}/publish`,
        method: 'PUT',
        body: { publish },
      }),
    }),
    getAttributeMigration: builder.query<any, void>({
      query: () => ({
        url: `/product-service/internal/v1/attribute-migration`,
        method: 'GET',
      }),
    }),
  }),
});

export const {
  useAddFullCategoryMutation,
  useGetProductsQuery,
  useGetProductsStatsQuery,
  useGetProductDetailsQuery,
  useGetProductBasicDetailsQuery,
  useApproveProductRequestMutation,
  useRejectProductRequestMutation,
  useEditProductRequestMutation,
  useUploadProductImageMutation,
  useUploadProductCertificateMutation,
  useUploadProductColourImageMutation,
  useUploadProductLogoMutation,
  useAddSellerProductMutation,
  useUpdateSellerProductMutation,
  useGetSellerProductFullDetailsQuery,
  useGetSellerProductsByAdminQuery,
  useLazyGetSellerProductsByAdminQuery,
  usePublishProductMutation,
  useAddSellerProductByAdminMutation,
  useEditSellerProductByAdminMutation,
  useDeleteSellerProductByAdminMutation,
  useUploadProductImageByAdminMutation,
  useUploadProductCertificateByAdminMutation,
  useUploadProductColorImageByAdminMutation,
  useUploadProductLogoByAdminMutation,
  usePublishProductByAdminMutation,
  useGetAttributeMigrationQuery,
  useGetProductBasicDetailsByAdminQuery,
} = productService;
